import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

const OWNERSHIP_TYPES = ["CONDO", "COOP", "CONDP", "RNTL", "RENTAL", "MULTI", "TOWNHOUSE"];
const BUILDING_TYPES = ["WALKUP", "ELEVATOR", "HIGH RISE", "BROWNSTONE", "TOWNHOUSE", "LOFT", "PREWAR", "NEW DEV"];
const LISTING_STATUS_OPTIONS = [
  "ACTIVE", "BACK ON MKT", "BOARD APPROVED", "CLOSED", "CLOSED THRU US",
  "COMING SOON", "CONTRACT SIGNED", "DELETE", "DELETED", "DELISTED",
  "FUTURE", "LSE OUT", "LSE SIGN", "OFF EXCLUSIVE", "OFFER ACCEPTED",
  "PERM OFF MKT", "RENTED", "RENTED THRU US", "SOLD", "TEMP OFF MKT",
];

export default function BuildingEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buildingForm, setBuildingForm] = useState({});
  const [editingListing, setEditingListing] = useState(null);
  const [listingForm, setListingForm] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const loadBuilding = async () => {
    try {
      const data = await authFetch(`/buildings/${id}`);
      setBuilding(data);
      setBuildingForm({
        total_units: data.total_units,
        total_floors: data.total_floors,
        year_built: data.year_built,
        year_renovated: data.year_renovated,
        ownership_type: data.ownership_type || "",
        building_type: data.building_type || "",
        building_tier: data.building_tier || "",
        doorman: data.doorman,
        elevator_in_building: data.elevator,
        concierge: data.concierge,
        laundry: data.laundry,
        storage: data.storage,
        healthclub: data.healthclub,
        pool: data.pool,
        courtyard: data.courtyard,
        roof_deck: data.roof_deck,
        all_units_washer_dryer: data.all_units_washer_dryer || false,
        all_units_dishwasher: data.all_units_dishwasher || false,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => { loadBuilding(); }, [id]);

  const saveBuilding = async () => {
    setSaving(true);
    try {
      await authFetch(`/buildings/${id}`, {
        method: "PATCH",
        body: JSON.stringify(buildingForm),
      });
      setMessage({ type: "success", text: "Building saved" });
      loadBuilding();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const startEditListing = (listing) => {
    setEditingListing(listing.id);
    setListingForm({
      unit_number: listing.unit_number || "",
      bedrooms: listing.bedrooms ?? "",
      bathrooms: listing.bathrooms ?? "",
      sqft: listing.sqft ?? "",
      list_price: listing.list_price ?? "",
      listing_status: listing.listing_status || "",
      floor: listing.floor || "",
      floor_line: listing.floor_line || "",
      dishwasher: listing.dishwasher ?? "",
      washer_dryer: listing.washer_dryer || "",
      monthly_taxes: listing.monthly_taxes ?? "",
      monthly_cc: listing.monthly_cc ?? "",
      ai_renovation: listing.ai_renovation ?? "",
      furnished: listing.furnished ?? false,
      block: listing.block || "",
      lot: listing.lot || "",
      borough: listing.borough || "",
      ownership_type: listing.ownership_type || "",
      created_date: listing.created_date ? listing.created_date.slice(0, 10) : "",
    });
  };

  const saveListing = async (listingId) => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(listingForm).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          if (["bedrooms", "bathrooms", "list_price", "monthly_taxes", "monthly_cc", "sqft"].includes(k)) {
            payload[k] = parseFloat(v);
          } else if (k === "ai_renovation") {
            payload.renovation_level = parseInt(v);
          } else if (k === "dishwasher") {
            payload[k] = v === true || v === "true";
          } else if (k === "furnished") {
            payload[k] = v === true || v === "true";
          } else {
            payload[k] = v;
          }
        }
      });
      await authFetch(`/listings/${listingId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setMessage({ type: "success", text: "Listing saved" });
      setEditingListing(null);
      loadBuilding();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const approveCoop = async () => {
    setSaving(true);
    try {
      await authFetch(`/buildings/${id}`, {
        method: "PATCH",
        body: JSON.stringify({}),
      });
      // Use the approve endpoint logic directly
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/buildings/approve-coops`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
        body: (() => {
          const csv = `address,zipcode\n${building.display_address},${building.postal_code}`;
          const blob = new Blob([csv], { type: "text/csv" });
          const fd = new FormData();
          fd.append("file", blob, "approve.csv");
          return fd;
        })(),
      });
      const data = await resp.json();
      setMessage({ type: "success", text: `Co-op approved: ${data.listings_approved || 0} listings moved to review` });
      loadBuilding();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSaving(false);
  };

  if (loading) return (
    <div><Header showLogout={true} />
      <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
    </div>
  );

  if (!building) return (
    <div><Header showLogout={true} />
      <div style={{ padding: "40px", textAlign: "center" }}>Building not found</div>
    </div>
  );

  const inputStyle = { padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", width: "100%" };
  const selectStyle = { ...inputStyle, backgroundColor: "#fff" };
  const labelStyle = { fontSize: "11px", fontWeight: "600", color: "#555", marginBottom: "2px", display: "block" };
  const sectionStyle = { backgroundColor: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" };
  const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px" };
  const boolStyle = (val) => ({
    padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid #ddd",
    backgroundColor: val ? "#e8f5e9" : "#f5f5f5", color: val ? "#2e7d32" : "#999",
  });

  const isCoop = building.ownership_type === "COOP" || building.ownership_type === "CONDP";
  const isApproved = building.coop_policies?.approved;

  // Group listings by unit
  const unitGroups = {};
  (building.listings || []).forEach((l) => {
    const key = l.unit_number || "—";
    if (!unitGroups[key]) unitGroups[key] = [];
    unitGroups[key].push(l);
  });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: "16px", fontSize: "13px" }}>
          <Link to="/buildings" style={{ color: "#1976d2", textDecoration: "none" }}>Buildings</Link>
          {" → "}
          <span>{building.display_address}</span>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", fontSize: "13px",
            backgroundColor: message.type === "success" ? "#e8f5e9" : "#ffebee",
            color: message.type === "success" ? "#2e7d32" : "#c62828",
          }}>
            {message.text}
          </div>
        )}

        {/* Building Header */}
        <div style={{ ...sectionStyle, borderLeft: "4px solid #1976d2" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px" }}>{building.display_address}</h1>
              <div style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
                {building.neighborhood} · {building.postal_code} · ID: {building.id}
                {building.se_url && (
                  <a href={building.se_url} target="_blank" rel="noreferrer" style={{ marginLeft: "12px", color: "#1976d2" }}>SE ↗</a>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              {isCoop && !isApproved && (
                <button onClick={approveCoop} disabled={saving}
                  style={{ padding: "8px 16px", backgroundColor: "#4caf50", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                  Approve Co-op
                </button>
              )}
              {isApproved && (
                <span style={{ padding: "6px 12px", backgroundColor: "#e8f5e9", color: "#2e7d32", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                  Co-op Approved
                </span>
              )}
              <button onClick={saveBuilding} disabled={saving}
                style={{ padding: "8px 16px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                {saving ? "Saving..." : "Save Building"}
              </button>
            </div>
          </div>

          {/* Building Fields */}
          <div style={gridStyle}>
            <div><label style={labelStyle}>Total Units</label>
              <input type="number" value={buildingForm.total_units ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, total_units: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Total Floors</label>
              <input type="number" value={buildingForm.total_floors ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, total_floors: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Year Built</label>
              <input type="number" value={buildingForm.year_built ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, year_built: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Year Renovated</label>
              <input type="number" value={buildingForm.year_renovated ?? ""} onChange={(e) => setBuildingForm({ ...buildingForm, year_renovated: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Ownership</label>
              <select value={buildingForm.ownership_type} onChange={(e) => setBuildingForm({ ...buildingForm, ownership_type: e.target.value })} style={selectStyle}>
                <option value="">—</option>
                {OWNERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={labelStyle}>Building Type</label>
              <select value={buildingForm.building_type} onChange={(e) => setBuildingForm({ ...buildingForm, building_type: e.target.value })} style={selectStyle}>
                <option value="">—</option>
                {BUILDING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={labelStyle}>Building Tier</label>
              <input type="text" value={buildingForm.building_tier} onChange={(e) => setBuildingForm({ ...buildingForm, building_tier: e.target.value })} style={inputStyle} /></div>
          </div>

          {/* Amenities */}
          <div style={{ marginTop: "16px" }}>
            <label style={{ ...labelStyle, marginBottom: "8px" }}>Amenities</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                ["doorman", "Doorman"], ["elevator_in_building", "Elevator"], ["concierge", "Concierge"],
                ["laundry", "Laundry"], ["storage", "Storage"], ["healthclub", "Gym"],
                ["pool", "Pool"], ["courtyard", "Courtyard"], ["roof_deck", "Roof Deck"],
              ].map(([key, label]) => (
                <button key={key} style={boolStyle(buildingForm[key])}
                  onClick={() => setBuildingForm({ ...buildingForm, [key]: !buildingForm[key] })}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Unit-level amenity flags */}
          <div style={{ marginTop: "16px" }}>
            <label style={{ ...labelStyle, marginBottom: "8px" }}>Unit Amenities (all units)</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                ["all_units_washer_dryer", "All units have W/D"],
                ["all_units_dishwasher", "All units have D/W"],
              ].map(([key, label]) => (
                <button key={key} style={boolStyle(buildingForm[key])}
                  onClick={() => {
                    const newVal = !buildingForm[key];
                    if (newVal) {
                      const confirmed = window.confirm(
                        `Are you sure? This will mark ALL listings in this building as having ${key === "all_units_washer_dryer" ? "washer/dryer" : "dishwasher"}.\n\nThis will update all existing units immediately.`
                      );
                      if (!confirmed) return;
                      // Save immediately with the flag
                      setBuildingForm({ ...buildingForm, [key]: true });
                      authFetch(`/buildings/${id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ [key]: true }),
                      }).then((res) => {
                        setMessage({ type: "success", text: res.message || "Updated" });
                        loadBuilding();
                      }).catch((err) => {
                        setMessage({ type: "error", text: err.message });
                        setBuildingForm({ ...buildingForm, [key]: false });
                      });
                      setTimeout(() => setMessage(null), 3000);
                    } else {
                      window.alert(
                        `Unchecking this flag will NOT update existing units.\n\nTo remove ${key === "all_units_washer_dryer" ? "W/D" : "D/W"} from individual units, please edit them manually.`
                      );
                      setBuildingForm({ ...buildingForm, [key]: false });
                      authFetch(`/buildings/${id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ [key]: false }),
                      }).then(() => loadBuilding())
                        .catch((err) => setMessage({ type: "error", text: err.message }));
                    }
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div style={{ marginTop: "16px", display: "flex", gap: "16px", fontSize: "13px", color: "#666" }}>
            <span>{building.active_sale_listings} active sales</span>
            <span>{building.active_rental_listings} active rentals</span>
            {building.zone_name && <span>Zone: {building.zone_name} ({building.zone_score})</span>}
            {building.dist_to_subway != null && <span>Subway: {building.dist_to_subway.toFixed(2)} mi</span>}
          </div>
        </div>

        {/* Listings by Unit */}
        <div style={sectionStyle}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px", borderBottom: "2px solid #000", paddingBottom: "8px" }}>
            Units ({Object.keys(unitGroups).length}) · {building.listings?.length || 0} listings
          </h2>

          {Object.entries(unitGroups).sort().map(([unit, listings]) => (
            <div key={unit} style={{ marginBottom: "12px", border: "1px solid #e0e0e0", borderRadius: "6px", overflow: "hidden" }}>
              {/* Unit Header */}
              <div style={{ padding: "10px 16px", backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "14px", borderBottom: "1px solid #e0e0e0" }}>
                Unit {unit === "—" ? "(no unit)" : unit} · {listings.length} listing{listings.length > 1 ? "s" : ""}
              </div>

              {/* Listings */}
              {listings.map((l) => (
                <div key={l.id} style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
                  {editingListing === l.id ? (
                    /* Edit Mode */
                    <div>
                      <div style={{ ...gridStyle, gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                        <div><label style={labelStyle}>Unit</label>
                          <input value={listingForm.unit_number} onChange={(e) => setListingForm({ ...listingForm, unit_number: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Beds</label>
                          <input type="number" step="0.5" value={listingForm.bedrooms} onChange={(e) => setListingForm({ ...listingForm, bedrooms: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Baths</label>
                          <input type="number" step="0.5" value={listingForm.bathrooms} onChange={(e) => setListingForm({ ...listingForm, bathrooms: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Sqft</label>
                          <input type="number" value={listingForm.sqft} onChange={(e) => setListingForm({ ...listingForm, sqft: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Price</label>
                          <input type="number" value={listingForm.list_price} onChange={(e) => setListingForm({ ...listingForm, list_price: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Status</label>
                          <select value={listingForm.listing_status} onChange={(e) => setListingForm({ ...listingForm, listing_status: e.target.value })} style={selectStyle}>
                            {LISTING_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select></div>
                        <div><label style={labelStyle}>Floor</label>
                          <input value={listingForm.floor} onChange={(e) => setListingForm({ ...listingForm, floor: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Reno Level</label>
                          <select value={listingForm.ai_renovation} onChange={(e) => setListingForm({ ...listingForm, ai_renovation: e.target.value })} style={selectStyle}>
                            <option value="">—</option>
                            {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                          </select></div>
                        <div><label style={labelStyle}>D/W</label>
                          <select value={String(listingForm.dishwasher)} onChange={(e) => setListingForm({ ...listingForm, dishwasher: e.target.value === "true" })} style={selectStyle}>
                            <option value="">—</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select></div>
                        <div><label style={labelStyle}>W/D</label>
                          <select value={listingForm.washer_dryer} onChange={(e) => setListingForm({ ...listingForm, washer_dryer: e.target.value })} style={selectStyle}>
                            <option value="">—</option>
                            <option value="Y">Yes (Unit)</option>
                            <option value="N">No</option>
                          </select></div>
                        <div><label style={labelStyle}>Taxes/mo</label>
                          <input type="number" value={listingForm.monthly_taxes} onChange={(e) => setListingForm({ ...listingForm, monthly_taxes: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>CC/mo</label>
                          <input type="number" value={listingForm.monthly_cc} onChange={(e) => setListingForm({ ...listingForm, monthly_cc: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Block</label>
                          <input value={listingForm.block} onChange={(e) => setListingForm({ ...listingForm, block: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Lot</label>
                          <input value={listingForm.lot} onChange={(e) => setListingForm({ ...listingForm, lot: e.target.value })} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Listed Date</label>
                          <input type="date" value={listingForm.created_date} onChange={(e) => setListingForm({ ...listingForm, created_date: e.target.value })} style={inputStyle} /></div>
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                        <button onClick={() => saveListing(l.id)} disabled={saving}
                          style={{ padding: "6px 16px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", fontSize: "12px", cursor: "pointer" }}>
                          Save
                        </button>
                        <button onClick={() => setEditingListing(null)}
                          style={{ padding: "6px 16px", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: "4px", fontSize: "12px", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: "16px", fontSize: "13px", flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{
                          padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600",
                          backgroundColor: l.sale_or_rental === "S" ? "#e3f2fd" : "#fff3e0",
                          color: l.sale_or_rental === "S" ? "#1565c0" : "#e65100",
                        }}>
                          {l.sale_or_rental === "S" ? "Sale" : "Rental"}
                        </span>
                        <strong>${l.list_price?.toLocaleString() || "—"}{l.sale_or_rental === "R" ? "/mo" : ""}</strong>
                        <span>{l.bedrooms === 0 ? "Studio" : `${l.bedrooms}BR`}/{l.bathrooms}BA</span>
                        {l.sqft && <span>{l.sqft.toLocaleString()} sqft</span>}
                        <span style={{ color: "#666" }}>{l.listing_status}</span>
                        {l.verified_at && <span style={{ padding: "1px 6px", borderRadius: "3px", backgroundColor: "#e8f5e9", color: "#2e7d32", fontSize: "10px", fontWeight: "600" }}>Verified</span>}
                        {l.convertible_enabled && <span style={{ padding: "1px 6px", borderRadius: "3px", backgroundColor: "#fff3e0", color: "#e65100", fontSize: "10px", fontWeight: "600" }}>Conv. to {l.convertible_bedrooms}BR</span>}
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                        {l.se_url && <a href={l.se_url} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: "#1976d2" }}>SE</a>}
                        {l.sale_or_rental === "S" && (
                          <a href={`/predict/${l.id}`} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: "#1976d2" }}>Analysis</a>
                        )}
                        <button onClick={() => startEditListing(l)}
                          style={{ fontSize: "11px", padding: "2px 8px", backgroundColor: "#f5f5f5", border: "1px solid #ddd", borderRadius: "3px", cursor: "pointer" }}>
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
